import { useMutation } from "@tanstack/react-query";
import { use } from "react";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { useDeleteCardFromGlobalSearchCache } from "./useDeleteCardFromGlobalSearchCache.ts";
import { useDeleteCardFromMySignedCardsCache } from "./useDeleteCardFromMySignedCardsCache.ts";
import { useDeleteCardFromProjectCache } from "./useDeleteCardFromProjectCache.ts";
import { useInvalidateCardErrorsCache } from "./useInvalidateCardErrorsCache.ts";
import { useRemoveCardFromEditViewsCache } from "./useRemoveCardFromEditViewsCache.ts";

export const useDeleteCard = (projectId: string) => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const deleteCardFromProjectCache = useDeleteCardFromProjectCache(projectId);
  const deleteCardFromGlobalSearchCache =
    useDeleteCardFromGlobalSearchCache(projectId);
  const deleteCardFromMySignedCardsCache =
    useDeleteCardFromMySignedCardsCache();
  const removeCardFromEditViewsCache = useRemoveCardFromEditViewsCache();
  const invalidateCardErrorsCache = useInvalidateCardErrorsCache();
  const { displayError, displaySuccess } = use(SnackbarContext);
  const { error, isPending, mutate, isError, reset } = useMutation({
    mutationFn: async (id: string): Promise<Response> => {
      const token = await getToken();
      const response = await fetch(`/api/projects/${projectId}/cards/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Deleting the card failed");
      }

      return response;
    },
    mutationKey: ["deleteCard"],
    onSuccess: (_, id: string) => {
      deleteCardFromProjectCache(id);
      deleteCardFromGlobalSearchCache(id);
      deleteCardFromMySignedCardsCache(id);
      removeCardFromEditViewsCache(id);
      invalidateCardErrorsCache();
      displaySuccess(translator("cardDeleted"));
    },
    onError: () => {
      displayError(translator("errorCardDelete"));
    },
  });

  return { mutate, isPending, error, isError, reset };
};
